'use client';

import React, {createContext, useContext, useMemo, ReactNode} from 'react';
import {Instructor, InstructorDetails} from '@udemy-v2/browse-course';
import {useFetchCoursesByInstructors} from '@/lib/hooks/use-fetch-courses-by-instructors';
import {TrackableDiscoveryItem} from '@udemy-v2/react-browse-event-tracking';
import {useClpPurchaseContext} from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import {TaughtCoursesRequestConfig} from '@/types/taught-courses-api';
import {BrowseCourseWithInstructorDetails} from '@/types/course';
import {useClpCourse} from './clp-course-provider.react-component';
import {useUserHasOrganization} from '@/lib/hooks/use-user-has-organization';

export interface CourseWithInstructorDetailsContextType {
    /** Instructor details for the course instructors */
    instructorDetails: InstructorDetails[];
    /** More courses by this course's instructor which also include instructor details */
    moreCoursesByInstructor: Record<
        string,
        TrackableDiscoveryItem<BrowseCourseWithInstructorDetails>[]
    >;
    isLoading: boolean;
    error: string | null;
}

// Simple type alias to specify that the instructor has a non-null url
type InstructorWithUrl = Instructor & {url: string};

/**
 * Context for the course with instructor details.
 */
export const CourseWithInstructorDetailsContext = createContext<
    CourseWithInstructorDetailsContextType | undefined
>(undefined);

export interface CourseWithInstructorDetailsProviderProps {
    children: ReactNode;
    instructors?: Instructor[];
    pageSize?: number;
}

export const CourseWithInstructorDetailsProvider = ({
    children,
    instructors = [],
    // since we are filtering out the current course,
    // we need to fetch 4 courses per instructor
    pageSize = 4,
}: CourseWithInstructorDetailsProviderProps) => {
    const currentCourse = useClpCourse();
    const {isPersonalPlanSubscriber} = useClpPurchaseContext();
    const hasOrganization = useUserHasOrganization();
    const instructorsWithUrls = useMemo(
        () => instructors?.filter((instructor) => instructor.url) as InstructorWithUrl[],
        [instructors],
    );

    // Create configs with both ID and URL
    const instructorConfigs = useMemo(
        () =>
            instructorsWithUrls.map(
                (instructor): TaughtCoursesRequestConfig => ({
                    userId: instructor.id,
                    instructorUrl: instructor.url,
                    pageSize,
                    isInUserSubscription: isPersonalPlanSubscriber,
                    organizationCoursesOnly: hasOrganization,
                }),
            ),
        [instructorsWithUrls, pageSize, isPersonalPlanSubscriber, hasOrganization],
    );

    const {coursesByInstructorsByUrl, instructorDetailsByUrl, isLoading, error} =
        useFetchCoursesByInstructors(instructorConfigs);

    // Array of InstructorDetails that preserves the order of instructors.
    // Taught-courses does not return user ids (slug ids only), so stamp the GraphQL
    // instructor.id which is authoritative for instructor community lookups.
    const instructorDetails = useMemo(
        () =>
            instructorsWithUrls
                .map((instructor) => {
                    const details = instructorDetailsByUrl[instructor.url];
                    if (!details) {
                        return undefined;
                    }
                    return {...details, id: instructor.id};
                })
                .filter((detail): detail is InstructorDetails => !!detail),
        [instructorDetailsByUrl, instructorsWithUrls],
    );

    // Filter out the current course from the more courses by instructor data
    const filteredMoreCoursesByInstructor = useMemo(() => {
        const filtered: Record<
            string,
            TrackableDiscoveryItem<BrowseCourseWithInstructorDetails>[]
        > = {};

        Object.entries(coursesByInstructorsByUrl).forEach(([instructorUrl, courses]) => {
            filtered[instructorUrl] = courses.filter(
                (course) => course.id !== currentCourse.id.toString(),
            );
        });

        return filtered;
    }, [coursesByInstructorsByUrl, currentCourse.id]);

    const contextValue = useMemo(
        () => ({
            instructorDetails,
            moreCoursesByInstructor: filteredMoreCoursesByInstructor,
            isLoading,
            error: error?.message ?? null,
        }),
        [instructorDetails, filteredMoreCoursesByInstructor, isLoading, error?.message],
    );

    return (
        <CourseWithInstructorDetailsContext.Provider value={contextValue}>
            {children}
        </CourseWithInstructorDetailsContext.Provider>
    );
};

export const useCourseWithInstructorDetailsContext = () => {
    const context = useContext(CourseWithInstructorDetailsContext);
    if (context === undefined) {
        throw new Error(
            'useCourseWithInstructorDetailsContext must be used within a CourseWithInstructorDetailsProvider',
        );
    }
    return context;
};
